import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Stack, Typography } from "@mui/material";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { addSpaceAfterComma, formatNumberWithCommas } from "../../../../Services/helper";
import { Theme } from "../../../../Styles/GlobalTheme";
import ColorBar from "./ColorBar";
import { TAX_TOOLTIP_TITLE } from "./constants";
import { calculateEstimatedTax, getEquipmentName, isObjectEmpty } from "./OrderHelper";

const MonthlyBillCart = ({
  setOrderTotalPrice,
  orderTotalPrice,
  submitOrderPayload,
  submitOrderArr,
  installation,
  promotion,
  serviceAddress,
  selectedFiber,
  addonContent,
}: any) => {
  const { ndsObj } = useSelector((state: any) => state.customerInfoDetails);
  const [showInstallation, setShowInstallation] = useState(false);
  const [sortedSubmitArr, setSortedSubmitArr] = useState([]);
  const [showDiscount, setShowDiscount] = useState(false);
  const [open, setOpen] = React.useState(false);
  const { selectedPlanDetails } = useSelector((state: any) => state.OrderUpgradeDetails);
  const hasInstallationDiscount = JSON.stringify(installation?.discounts)?.includes("FREEINSTALL");

  const toNumber = (value: any) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const ndsDiscounts = (selectedPlanDetails?.discounts || []).filter(
    (item: any) => toNumber(item?.price) !== 0
  );
  const promoDiscounts = (promotion || []).filter(
    (item: any) =>
      item?.startDate && toNumber(item?.price) !== 0 && item?.expired?.toLowerCase() !== "y"
  );
  const appliedDiscounts = ndsObj?.isNDS ? ndsDiscounts : promoDiscounts;

  const tabletView = window.screen.width <= 1440;

  const handleTooltipClose = () => {
    setOpen(false);
  };

  const handleTooltipOpen = () => {
    setOpen(true);
  };
  const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: "#EEEEEE",
      color: "rgba(0, 0, 0, 0.87)",
      boxShadow: theme.shadows[1],
      fontSize: 11,
      padding: "10px",
    },
  }));

  interface cartDataProps {
    name: string;
    price: string;
    priceAfterDiscount?: any;
    isPerMonth?: any;
    productId?: any;
    sku: string;
    contentSku: any;
    groupType: string;
  }

  useEffect(() => {
    let findNewRouter = submitOrderArr?.find(
      (item: any) =>
        (item?.groupType === "Router" && item?.productId !== "BYO_ROUTER") ||
        item?.groupType === "Equipment"
    );
    setShowInstallation(findNewRouter);
    if (submitOrderArr && submitOrderArr?.length > 0) {
      let total = submitOrderArr?.reduce((sum: any, item: any) => {
        // Check if the item's name is not 'installation'
        if (item?.name !== "Installation") {
          if (item?.isPerMonth && ndsObj?.isNDS && item?.productId !== "WHW") {
            return sum + parseFloat(item?.priceAfterDiscount ?? item?.price);
          }
          return sum + parseFloat(item?.price);
        }
        return sum;
      }, 0);
      setShowDiscount(appliedDiscounts.length > 0);
      total += appliedDiscounts.reduce((sum: number, item: any) => sum + toNumber(item?.price), 0);
      const getTax: any = calculateEstimatedTax(
        submitOrderArr,
        ndsObj?.isNDS,
        submitOrderPayload?.addressInfo?.billingAddress?.stateOrProvince,
        installation
      );

      setOrderTotalPrice({
        ...orderTotalPrice,
        totalPrice: total,
        extimatedTax: getTax?.estimatedTax,
        installTax: getTax?.installTax,
      });
    }
    const priority = (item: any) => {
      if (item.__typename === "SingleAvailablePlan") return 0; // plan
      if (item.groupType === "Router") return 1; // router
      if (item.groupType === "Equipment") return 2; // equipment
      if (item.groupType === "Security Addons") return 3; // security
      if (item.groupType === "Voice Addon") return 4; // voice
      return 5; // others
    };

    const sortedArray = submitOrderArr.slice().sort((a: any, b: any) => {
      return priority(a) - priority(b);
    });
    setSortedSubmitArr(sortedArray);
  }, [submitOrderArr]);

  return (
    <Box my={4}>
      <ColorBar />
      <Stack justifyContent={"center"} alignItems={"center"} my={4} spacing={2}>
        <Typography
          fontSize={{ xs: "20px", md: "24px" }}
          fontWeight={600}
          color={Theme.palette.primary.main}>
          Your New Monthly Bill
        </Typography>
        <Typography textAlign="center">{addSpaceAfterComma(serviceAddress)}</Typography>
      </Stack>
      {sortedSubmitArr &&
        sortedSubmitArr?.length > 0 &&
        sortedSubmitArr?.map((data: any, index: number) => {
          const { name, price, isPerMonth, productId, sku, groupType, contentSku } = data;
          return (
            <Box
              py={2.6}
              sx={{ borderTop: index !== 0 ? `1px solid ${Theme.palette.grey[300]}` : "none" }}>
              <Stack direction={"row"} width={"100%"} justifyContent={"space-between"}>
                <Typography fontSize={{ xs: "16px", md: "18px" }}>
                  {productId === selectedFiber.productId && selectedFiber.updateToWifi6
                    ? "Whole Home WiFi"
                    : groupType === "Router"
                      ? (getEquipmentName(
                          contentSku && contentSku !== "" ? contentSku : sku,
                          addonContent
                        ) ?? name)
                      : name}
                </Typography>
                <Typography
                  fontSize={{
                    xs: "16px",
                    md: "18px",
                  }}>{`${formatNumberWithCommas(price ?? 0, ["LLSTA", "LLFED"].includes(productId) ? 2 : 0)}/mo`}</Typography>
              </Stack>
              {showDiscount &&
                isPerMonth &&
                appliedDiscounts.map((discount: any, index: number) => (
                  <Stack
                    key={`${discount?.discountCode || discount?.discountBillDescription || "discount"}-${index}`}
                    direction={"row"}
                    width={"100%"}
                    justifyContent={"space-between"}>
                    <Typography
                      fontSize={{ xs: "12px", md: "14px" }}
                      color={Theme.palette.primary.main}
                      sx={{ maxWidth: { xs: "200px", md: "500px" } }}>
                      {discount?.discountBillDescription}
                    </Typography>
                    <Typography fontSize={"14px"} color={Theme.palette.primary.main}>
                      {formatNumberWithCommas(discount?.price)}
                    </Typography>
                  </Stack>
                ))}
            </Box>
          );
        })}
      {installation &&
        !isObjectEmpty(installation) &&
        (showInstallation || selectedPlanDetails?.productId === "FIBER_10G") && (
          <Box pb={2}>
            <Stack
              pt={1.5}
              direction={"row"}
              width={"100%"}
              justifyContent={"space-between"}
              sx={{ borderTop: `1px solid ${Theme.palette.grey[300]}` }}>
              <Typography fontSize={{ xs: "16px", md: "18px" }}>
                Installation (One time charge)
              </Typography>
              <Stack direction="column" alignItems="flex-end">
                <Typography fontSize={{ xs: "16px", md: "18px" }}>
                  ${installation?.price}
                </Typography>
              </Stack>
            </Stack>
            {hasInstallationDiscount && installation?.price > 0 && (
              <Stack direction={"row"} width={"100%"} justifyContent={"space-between"}>
                <Typography
                  fontSize={{ xs: "12px", md: "14px" }}
                  color={Theme.palette.primary.main}
                  sx={{ maxWidth: { xs: "200px", md: "500px" } }}>
                  Discount
                </Typography>
                <Typography fontSize={"14px"} color={Theme.palette.primary.main}>
                  -$
                  {Math.abs(installation?.price)}
                </Typography>
              </Stack>
            )}
          </Box>
        )}
      <Stack
        py={2.6}
        direction={"row"}
        width={"100%"}
        justifyContent={"space-between"}
        sx={{ borderTop: `1px solid ${Theme.palette.grey[300]}` }}>
        <Stack direction={"row"} alignItems={"center"} spacing={1}>
          <Typography fontSize={{ xs: "16px", md: "18px" }}>Estimated Monthly Tax</Typography>

          {tabletView ? (
            <ClickAwayListener onClickAway={handleTooltipClose}>
              <div>
                <LightTooltip
                  placement="top"
                  PopperProps={{
                    disablePortal: true,
                  }}
                  onClose={handleTooltipClose}
                  open={open}
                  disableFocusListener
                  disableHoverListener
                  disableTouchListener
                  title={TAX_TOOLTIP_TITLE}>
                  <IconButton sx={{ padding: 0 }} onClick={handleTooltipOpen}>
                    <InfoOutlinedIcon
                      sx={{ color: Theme.palette.secondary.main, fontSize: "16px" }}
                    />
                  </IconButton>
                </LightTooltip>
              </div>
            </ClickAwayListener>
          ) : (
            <>
              <LightTooltip title={TAX_TOOLTIP_TITLE} placement="top">
                <IconButton sx={{ padding: 0 }}>
                  <InfoOutlinedIcon
                    sx={{ color: Theme.palette.secondary.main, fontSize: "16px" }}
                  />
                </IconButton>
              </LightTooltip>
            </>
          )}
        </Stack>
        <Typography fontSize={{ xs: "16px", md: "18px" }}>
          ${(Math.round(orderTotalPrice?.extimatedTax * 100) / 100)?.toFixed(2)}/mo
        </Typography>
      </Stack>
      <Stack
        py={2.6}
        direction={"row"}
        width={"100%"}
        justifyContent={"space-between"}
        sx={{ borderTop: `1px solid ${Theme.palette.grey[300]}` }}>
        <Typography fontSize={{ xs: "16px", md: "18px" }}>Due Monthly + Tax</Typography>
        <Typography fontSize={{ xs: "16px", md: "18px" }}>
          $
          {(
            Math.round((orderTotalPrice?.totalPrice + orderTotalPrice?.extimatedTax) * 100) / 100
          )?.toFixed(2)}
          /mo
        </Typography>
      </Stack>

      <Stack py={2} direction={"row"} width={"100%"} justifyContent={"space-between"}>
        <Typography color={Theme.palette.primary.main} fontSize={{ xs: "16px", md: "18px" }}>
          Total Due Today
        </Typography>
        <Typography color={Theme.palette.primary.main} fontSize={{ xs: "16px", md: "18px" }}>
          $0
        </Typography>
      </Stack>

      <ColorBar />
    </Box>
  );
};

export default MonthlyBillCart;
